import {
  ConversationMessageEntity,
  ConversationRecipientEntity,
  ConversationResponseEntity,
  useMessageGetConversationMessages,
  UserEntity,
} from "@/src/hooks/api/generated";

import { useAuth } from "./useAuth";
import { useChat } from "./useChat";
import { useNavigate } from "./useNavigate";

interface IGetMessages {
  recipientIds?: number[];
  conversationId?: number;
  page?: number;
  pageSize?: number;
}
export interface IUseChatDataReturn {
  deleteSelectedConversation: () => void;
  deleteConversationBaseOnId: (conversationIdForDelete: number) => void;
  markConversationAsBlocked: (conversationIdForMarkAsBlocked: number) => void;
  changeSearchConnectionStatus: (value?: boolean) => void;
  userStatusChanges: (id: number, statues: boolean) => void;
  setSelectedConversationByConversationData: (
    conversation: ConversationResponseEntity
  ) => void;
  setSelectedConversationByUserData: (user?: UserEntity) => void;
  getUserMessages: ({
    recipientIds,
    conversationId,
    page,
    pageSize,
  }: IGetMessages) => void;
  appendNewMessage: (messages: ConversationMessageEntity) => void;
  appendConversationToTop: (messages: ConversationMessageEntity) => void;
  getOtherConversationUser: (
    recipients: ConversationRecipientEntity[]
  ) => UserEntity | undefined;
  toggleConversationReadStatus: (
    conversationId: number,
    readStatus: boolean
  ) => void;
}

export function useChatFunctions(): IUseChatDataReturn {
  const { authData } = useAuth();
  const { pushQueryParam } = useNavigate();
  const { mutate: getMessages } = useMessageGetConversationMessages();

  const {
    conversations,
    selectedConversation,
    setSelectedConversation,
    setConversations,
    messages,
    setMessages,
    chatMetaData,
    setChatMetaData,
  } = useChat();

  const deleteSelectedConversation = (): void => {
    if (!selectedConversation || !conversations) {
      return;
    }
    const conversationWithoutDeletedOne = conversations?.data.filter(
      (conversation) => conversation.id !== selectedConversation.conversationId
    );
    if (conversationWithoutDeletedOne && conversations) {
      setSelectedConversation(undefined);
      setMessages?.(undefined);
      setConversations({
        ...conversations,
        data: conversationWithoutDeletedOne,
      });

      const [firstConversation] = conversationWithoutDeletedOne;
      if (firstConversation) {
        const firstRecipient = getOtherConversationUser(
          firstConversation.conversationRecipients
        );
        if (firstRecipient) {
          pushQueryParam({
            key: "recipient",
            value: firstRecipient?.id.toString(),
          });
        }
      }
    }
  };

  const deleteConversationBaseOnId = (
    conversationIdForDelete: number
  ): void => {
    if (!selectedConversation || !conversations) {
      return;
    }
    const conversationWithoutDeletedOne = conversations?.data.filter(
      (conversation) => conversation.id !== conversationIdForDelete
    );

    if (conversationWithoutDeletedOne && conversations) {
      if (selectedConversation.conversationId === conversationIdForDelete) {
        setSelectedConversation(undefined);
        setMessages?.(undefined);
        setConversations({
          ...conversations,
          data: conversationWithoutDeletedOne,
        });

        const [firstConversation] = conversationWithoutDeletedOne;
        if (firstConversation) {
          const firstRecipient = getOtherConversationUser(
            firstConversation.conversationRecipients
          );
          if (firstRecipient) {
            pushQueryParam({
              key: "recipient",
              value: firstRecipient?.id.toString(),
            });
          }
        }
      } else {
        setConversations({
          ...conversations,
          data: conversationWithoutDeletedOne,
        });
      }
    }
  };

  const markConversationAsBlocked = (
    conversationIdForMarkAsBlocked: number
  ): void => {
    if (!selectedConversation || !conversations) {
      return;
    }
    const updatedConversationsWithBlockStatus = conversations?.data.map(
      (conversation) => {
        if (conversation.id === conversationIdForMarkAsBlocked) {
          return { ...conversation, isBlocked: true };
        }
        return conversation;
      }
    );

    if (updatedConversationsWithBlockStatus && conversations) {
      if (
        selectedConversation.conversationId === conversationIdForMarkAsBlocked
      ) {
        setSelectedConversation({ ...selectedConversation, isBlocked: true });
      }
    }
    setConversations({
      ...conversations,
      data: updatedConversationsWithBlockStatus,
    });
  };

  const changeSearchConnectionStatus = (value: boolean = true): void => {
    setChatMetaData({ ...chatMetaData, isSearchConnectionOpen: value });
  };

  const userStatusChanges = (userId: number, newStatus: boolean): void => {
    if (selectedConversation?.userId === userId) {
      setSelectedConversation({
        ...selectedConversation,
        userStatus: newStatus ? "online" : "offline",
      });
    }
    const conversationsWithUpdateUserStatus = conversations?.data.map(
      (conversation) => {
        const conversationRecipientsWithNewStatus =
          conversation.conversationRecipients.map((recipient) => {
            if (recipient.user.id === userId) {
              return {
                ...recipient,
                user: { ...recipient.user, online: newStatus },
              };
            }
            return recipient;
          });
        return {
          ...conversation,
          conversationRecipients: conversationRecipientsWithNewStatus,
        };
      }
    );
    if (conversationsWithUpdateUserStatus && conversations) {
      setConversations({
        ...conversations,
        data: conversationsWithUpdateUserStatus,
      });
    }
  };

  const setSelectedConversationByConversationData = (
    conversation: ConversationResponseEntity
  ): void => {
    const firstRecipient = getOtherConversationUser(
      conversation.conversationRecipients
    );
    toggleConversationReadStatus(conversation.id, false);

    if (firstRecipient) {
      setSelectedConversation({
        title: `${firstRecipient?.first_name} ${firstRecipient?.last_name}`,
        subtitle: firstRecipient?.userTaxonomy?.job_title
          ? `${firstRecipient?.userTaxonomy?.job_title}  ${
              firstRecipient?.userTaxonomy?.organization_name
                ? `at ${firstRecipient?.userTaxonomy?.organization_name}`
                : ""
            }`
          : "",
        logoSrc: firstRecipient?.userTaxonomy?.image?.public_id,
        userPronounce: firstRecipient?.pronoun,
        conversationId: conversation.id,
        userId: firstRecipient.id,
        isBlocked: conversation.isBlocked,
        userStatus: firstRecipient?.online ? "online" : "offline",
      });
    }
  };

  const setSelectedConversationByUserData = (
    user: UserEntity | undefined
  ): void => {
    if (!user) {
      return;
    }
    setSelectedConversation({
      title: `${user.first_name} ${user.last_name}`,
      subtitle: user.userTaxonomy.job_title
        ? `${user.userTaxonomy.job_title}  ${
            user.userTaxonomy.organization_name
              ? `at ${user.userTaxonomy.organization_name}`
              : ""
          }`
        : "",
      logoSrc: user?.userTaxonomy?.image?.public_id,
      userPronounce: user.pronoun,
      recipientIds: [user.id],
      userId: user.id,
    });
  };

  const getUserMessages = ({
    recipientIds,
    conversationId,
    page = 1,
    pageSize = 10,
  }: IGetMessages): void => {
    getMessages(
      {
        data: { page, pageSize, recipientIds, conversationId },
      },
      {
        onSuccess: (messagesData) => {
          setMessages?.({
            ...messagesData,
            data: messagesData.data.reverse(),
          });
        },
        onError: () => {
          setMessages?.(undefined);
        },
      }
    );
  };

  const toggleConversationReadStatus = (
    conversationId: number,
    readStatus: boolean
  ): void => {
    const tempConversationData = conversations?.data.map((conversation) => {
      if (conversation.id === conversationId) {
        return { ...conversation, isUnread: readStatus };
      }
      return conversation;
    });
    if (tempConversationData && conversations) {
      setConversations({ ...conversations, data: tempConversationData });
    }
  };

  const getOtherConversationUser = (
    recipients: ConversationRecipientEntity[]
  ): UserEntity | undefined => {
    const firstRecipient = recipients.find(
      (recipient) => recipient.user.id !== authData?.id
    )?.user;
    return firstRecipient;
  };

  const appendNewMessage = (newMessage: ConversationMessageEntity): void => {
    if (
      messages?.data &&
      newMessage.conversation.id === selectedConversation?.conversationId
    ) {
      const tempMessagesData = messages.data;
      tempMessagesData.push(newMessage);
      setMessages?.({ ...messages, data: tempMessagesData });
    }
  };

  const appendConversationToTop = (
    newMessage: ConversationMessageEntity
  ): void => {
    const newMessageConversation = newMessage.conversation;
    const tempConversations = conversations?.data.filter(
      (conversation) => conversation.id !== newMessageConversation.id
    );
    tempConversations?.unshift({
      ...newMessageConversation,
      lastMessage: newMessage,
      isUnread: true,
    });

    if (!messages) {
      setMessages?.({
        page: 1,
        pageSize: 10,
        total: 1,
        data: [newMessage],
      });
    }

    if (tempConversations && conversations) {
      setConversations({ ...conversations, data: tempConversations });
    }
  };

  return {
    userStatusChanges,
    deleteSelectedConversation,
    deleteConversationBaseOnId,
    markConversationAsBlocked,
    changeSearchConnectionStatus,
    setSelectedConversationByConversationData,
    setSelectedConversationByUserData,
    getUserMessages,
    appendNewMessage,
    appendConversationToTop,
    getOtherConversationUser,
    toggleConversationReadStatus,
  };
}
